export type NotificationAudience =
  | 'sector_lead'
  | 'delegate'
  | 'creator'
  | 'executives'
  | 'requester'
  | 'stock_admins'
  | 'letter_holders'
  | 'employee'
  | 'payroll'
  | 'affected_person'
  | 'all_employees'
  | 'manager'
  | 'other_executives'
  /** APP-148 D18: told when someone questions what a Stock Admin did for them. */
  | 'system_admins';
export type RequiredEmailFamily =
  'election' | 'award_announced' | 'delegate_entry' | 'approval_needed';
export type EmailDelivery = 'immediate' | 'digest';
/**
 * A person's starting choice for an optional channel, where it isn't on (APP-138 §D3): until they
 * choose, that channel is off for them. The organization's switches still come first.
 */
export interface PersonalDefault {
  readonly email?: false;
  readonly teams?: false;
}
export interface NotificationDefinition {
  readonly matrixRow: number | null;
  readonly label: string;
  readonly audiences: readonly NotificationAudience[];
  readonly defaultEmail: boolean;
  readonly defaultInApp: boolean;
  readonly emailSupported: boolean;
  readonly alwaysEmail: boolean;
  readonly requiredFamilies: readonly RequiredEmailFamily[];
  readonly emailDelivery: EmailDelivery;
  /**
   * The bell can't be switched off, by the organization or the person: shown as "Always in the
   * bell" (K25's getting started; APP-138 §D3's feedback notices).
   */
  readonly bellAlways: boolean;
  readonly personalDefault?: PersonalDefault;
}
const entry = (
  matrixRow: number | null,
  label: string,
  audiences: readonly NotificationAudience[],
  defaultEmail: boolean,
  requiredFamilies: readonly RequiredEmailFamily[] = [],
  extra: Partial<
    Pick<
      NotificationDefinition,
      | 'defaultInApp'
      | 'emailSupported'
      | 'alwaysEmail'
      | 'emailDelivery'
      | 'bellAlways'
      | 'personalDefault'
    >
  > = {},
): NotificationDefinition => ({
  matrixRow,
  label,
  audiences,
  defaultEmail,
  requiredFamilies,
  defaultInApp: true,
  emailSupported: true,
  alwaysEmail: false,
  emailDelivery: 'immediate',
  bellAlways: false,
  ...extra,
});

/** N4 has 23 rows. M4 additional emails stay off; source annotations never become product copy. */
export const notificationCatalog = {
  // APP-049: targets are resolved from protected Spot authority, never ordinary staff roles.
  spot_nomination_received: entry(null, 'Spot nomination received', ['creator'], false),
  spot_committee_action_needed: entry(
    null,
    'Spot nomination to review',
    ['affected_person'],
    true,
    ['approval_needed'],
  ),
  spot_supervisor_action_needed: entry(null, 'Spot recommendation to decide', ['manager'], true, [
    'approval_needed',
  ]),
  spot_funding_action_needed: entry(null, 'Spot funding to decide', ['executives'], true, [
    'approval_needed',
  ]),
  spot_funding_decided: entry(null, 'Spot funding decision', ['affected_person'], true),
  // APP-264 (Tom, 2026-11-01): information only, when the Spot Awards pool goes over budget.
  spot_pool_over_budget: entry(null, 'Spot Awards pool over budget', ['executives'], true),
  // APP-243 (change 16): the committee hears the award was approved; the letter is checked by the
  // Stock Admin team.
  spot_fulfillment_action_needed: entry(null, 'Spot award approved', ['affected_person'], true),
  spot_nomination_decided: entry(null, 'Spot nomination outcome', ['creator'], false),
  // APP-226: the committee and the nominee's manager, when someone cancels before the letter.
  spot_nomination_cancelled: entry(null, 'Spot nomination cancelled', ['affected_person'], true),
  // APP-227: the manager whose decision someone recorded for them, always told, as "Done for you".
  spot_supervisor_decision_recorded: entry(
    null,
    'Spot decision recorded for you',
    ['manager'],
    true,
    [],
    { bellAlways: true, alwaysEmail: true },
  ),
  spot_statement_check_needed: entry(
    null,
    'Spot earning statement to check',
    ['affected_person'],
    true,
  ),
  self_recognition_approval_needed: entry(null, 'Executive approval needed', ['executives'], true, [
    'approval_needed',
  ]),
  delegate_submitted: entry(
    1,
    'Delegate entry submitted',
    ['sector_lead'],
    true,
    ['delegate_entry'],
    { alwaysEmail: true },
  ),
  delegate_approval_needed: entry(
    2,
    'Delegate approval needed',
    ['sector_lead'],
    true,
    ['delegate_entry', 'approval_needed'],
    { alwaysEmail: true },
  ),
  delegate_decision: entry(3, 'Delegate entry decision', ['delegate'], false),
  // APP-275: nobody approves an Executive's award for a sector first, so its Sector Lead is told.
  executive_sector_award: entry(
    null,
    'An Executive entered an award for your sector',
    ['sector_lead'],
    true,
  ),
  overage_approval_needed: entry(4, 'Over-budget approval needed', ['executives'], true, [
    'approval_needed',
  ]),
  overage_decision: entry(5, 'Over-budget decision', ['creator', 'sector_lead'], false),
  paa_taa_approval_needed: entry(6, 'PAA or TAA approval needed', ['executives'], true, [
    'approval_needed',
  ]),
  executive_award_created: entry(7, 'Executive Discretionary Bonus created', ['executives'], true),
  requester_confirmation: entry(8, 'Request received', ['requester'], false),
  executive_award_final_summary: entry(
    9,
    'Executive Discretionary Bonus summary',
    ['executives'],
    true,
  ),
  stock_admin_queue: entry(10, 'Request ready for Stock Admin', ['stock_admins'], true),
  stock_admin_digest: entry(11, 'Stock Admin summary', ['stock_admins'], true, [], {
    defaultInApp: false,
    emailDelivery: 'digest',
  }),
  // APP-245 D16, the canvas's D5 (Tom, 2026-10-31): the holder has something to do, so the email is on by default
  // for every award type; for an Executive Discretionary requester too, the same release notice.
  letters_released: entry(12, 'Letters ready to hand out', ['letter_holders', 'requester'], true),
  // APP-245 D12: the employee, when their letter's holder sends it from Letters to hand out. Only
  // once they can see the award, so it follows "Your award is ready" and never comes before it.
  letter_sent: entry(null, 'Your letter was sent to you', ['employee'], true),
  // APP-245 D2 (the canvas's page 4): a Spot letter released to its holder and still not handed
  // out or sent 3 business days later. Once, to whoever holds it then.
  letter_handout_reminder: entry(null, 'A letter is still to hand out', ['letter_holders'], true),
  election_opened: entry(13, 'Your award and election are ready', ['employee'], true, [
    'election',
    'award_announced',
  ]),
  // K24 (Agreed): "award announced" is an email the organization requires, so it's built on (APP-092).
  award_announced: entry(14, 'Your award is ready', ['employee'], true, ['award_announced']),
  election_reminder: entry(15, 'Your election needs a choice', ['employee'], true, ['election']),
  election_received: entry(16, 'Election received', ['employee', 'stock_admins'], false, [
    'election',
  ]),
  election_past_deadline: entry(17, 'Election past the deadline', ['stock_admins'], true),
  payroll_ready: entry(18, 'Lines ready for Payroll', ['payroll'], true),
  earning_statement_attached: entry(19, 'Earning statement added', ['stock_admins'], true),
  shareworks_ready: entry(20, 'Rows ready for Shareworks', ['stock_admins'], true, [], {
    emailDelivery: 'digest',
  }),
  role_changed: entry(21, 'Role or delegation changed', ['affected_person', 'sector_lead'], false),
  // K25: always in the bell, never emailed.
  getting_started: entry(22, 'Get started with MyRewards', ['affected_person'], false, [], {
    emailSupported: false,
    bellAlways: true,
  }),
  go_live_welcome: entry(23, 'Welcome to MyRewards', ['all_employees'], false),
  // M4: this whole event is disabled until the separate election manager-nudge setting enables it.
  manager_nudge: entry(null, 'An election needs a follow-up', ['manager'], true),
  // M7: other Executives receive an informational notice, never another approval task.
  executive_approval_handled: entry(null, 'Approval handled', ['other_executives'], false),
  // APP-042: the creator can correct a request returned by the Stock Admin team.
  check_returned: entry(null, 'Request returned by Stock Admin', ['creator', 'sector_lead'], true),
  // APP-092: whoever entered a PAA, TAA or self-recognition award hears the Executives' decision
  // (the Sector Lead too, when a delegate entered it). M4: the email is built, off until enabled.
  executive_decision: entry(null, 'Executive decision', ['creator', 'sector_lead'], false),
  // APP-130 D16: whoever released an award's first letters hears when the letter the rest waited
  // on is ready. A person makes, reviews and releases them; nothing is made automatically.
  letter_template_ready: entry(null, 'Missing letter ready', ['affected_person'], true),
  // APP-130 D18: an award was sent with a letter that can't be made yet; told once per gap set.
  letter_gap: entry(null, 'Letter missing for an award', ['stock_admins'], true),
  // APP-138 §D3, §D4: the sender of feedback (each duplicate's sender for their own report) hears
  // the MyRewards team's replies and the status they see change. Always in the bell; a reply's
  // email and Teams post are on, a status change's off until the person turns them on.
  feedback_replied: entry(null, 'Reply to your feedback', ['affected_person'], true, [], {
    bellAlways: true,
  }),
  feedback_status_changed: entry(
    null,
    'Your feedback’s status changed',
    ['affected_person'],
    true,
    [],
    { bellAlways: true, personalDefault: { email: false, teams: false } },
  ),
  // APP-148 D18: whoever a Stock Admin acted for is always told, in the bell and by an email
  // that can't be turned off (and in Teams when they use it). The team hears when the person
  // questions it; who asked, the team and (when returned) whoever entered it, when it's settled.
  acted_for_you: entry(null, 'Done for you', ['affected_person'], true, [], {
    alwaysEmail: true,
    bellAlways: true,
  }),
  acted_for_questioned: entry(
    null,
    'Done for someone: not what they asked',
    ['system_admins', 'stock_admins'],
    true,
    [],
    { bellAlways: true },
  ),
  acted_for_settled: entry(
    null,
    'Done for someone: settled',
    ['affected_person', 'stock_admins', 'system_admins', 'creator'],
    true,
    [],
    { bellAlways: true },
  ),
} as const satisfies Readonly<Record<string, NotificationDefinition>>;
export type NotificationEvent = keyof typeof notificationCatalog;
export function isNotificationEvent(value: string): value is NotificationEvent {
  return Object.hasOwn(notificationCatalog, value);
}
export const defaultRequiredEmailFamilies: readonly RequiredEmailFamily[] = [
  'election',
  'award_announced',
  'delegate_entry',
  'approval_needed',
];
